import { useRef } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useHistoryLeaveBlocker } from '@/services/navigation-guard/use-history-leave-blocker';
import { useAnalytics } from '@/services/analytics';
import { getApiClientErrorDetailFromError } from '@/services/api';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useRouter } from '@tanstack/react-router';
import { useCreateSqlImport, useUpdateSqlImport, type SqlImportReply } from '@/services/db/sql-import/mutations';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { SQL_DATABASE_CATALOG, sqlImportSyncTargetKind } from '@/features/sql-sync';
import { isEmptyString } from '@/utils/string';
import type { SqlImportDraft } from './draft';
import { SQL_SYNC_IMPORT_ROUTE_PATHS } from '../hooks';

type UseSaveSqlImportArgs = {
  workspaceId: string;
  type: SqlDatabaseType;
  draft: SqlImportDraft;
  /** The saved import the draft belongs to, once it has been saved. */
  savedId: string | null;
  syncTarget: string;
  /** A save landed; the caller re-baselines on the draft that was sent, not on what was typed since. */
  onSaved: (reply: SqlImportReply, sentDraft: SqlImportDraft) => void;
  /** The draft was saved for the first time from the header. */
  onFirstDraftSaved: (id: string) => void;
};

/**
 * The wizard's two writes. Saving a draft keeps the user in the wizard; publishing
 * activates the import and hands off to its detail page. Either failure is reported and leaves the wizard where it was.
 */
export function useSaveSqlImport({
  workspaceId,
  type,
  draft,
  savedId,
  syncTarget,
  onSaved,
  onFirstDraftSaved,
}: UseSaveSqlImportArgs) {
  const snackbars = useSnackbars();
  const safeNavigate = useSafeNavigate();
  const router = useRouter();
  const { track } = useAnalytics();
  const createSqlImport = useCreateSqlImport({ workspaceId: Number(workspaceId) });
  const updateSqlImport = useUpdateSqlImport({ workspaceId: Number(workspaceId) });
  // Two clicks before the next render must not become two requests (and two published imports):
  // the pending flag from the mutation lands a render late, so the latch is a ref.
  const inFlight = useRef(false);

  const save = async (importStatus: 'draft' | 'published'): Promise<SqlImportReply | null> => {
    if (inFlight.current) {
      return null;
    }
    inFlight.current = true;
    const sentDraft = draft;
    const body = { ...sentDraft, database_type: type, import_status: importStatus };
    try {
      const reply =
        savedId == null
          ? await createSqlImport.mutateAsync(body)
          : await updateSqlImport.mutateAsync({ sqlImportId: savedId, ...body });
      onSaved(reply, sentDraft);
      return reply;
    } catch (error) {
      const detail = getApiClientErrorDetailFromError(error);
      snackbars.create({
        message: isEmptyString(detail) ? 'Something went wrong. Please try again.' : detail,
        variant: 'critical',
      });
      return null;
    } finally {
      inFlight.current = false;
    }
  };

  /**
   * Persist the draft as it stands. From the header the URL moves onto the saved draft; the
   * leave guard saves without touching the URL, because it retries the aborted navigation right
   * after and a query-param write would queue a competing navigation back into the wizard.
   */
  const saveDraft = async ({ withUrl }: { withUrl: boolean }): Promise<boolean> => {
    const wasNew = savedId == null;
    const reply = await save('draft');
    if (reply != null && wasNew && withUrl) {
      onFirstDraftSaved(reply.sql_import.id);
    }
    return reply != null;
  };

  const publish = async (): Promise<void> => {
    const reply = await save('published');
    if (reply == null) {
      return;
    }
    void track('sqlImports', 'createSyncStarted', {
      'Database Type': SQL_DATABASE_CATALOG[type].name,
      'Sync Target': sqlImportSyncTargetKind(syncTarget),
    });
    safeNavigate(
      router.buildLocation({
        to: SQL_SYNC_IMPORT_ROUTE_PATHS.migrated,
        params: { workspaceId, type, sqlImportId: reply.sql_import.id },
      }).href,
    );
  };

  return { isSaving: createSqlImport.isPending || updateSqlImport.isPending, saveDraft, publish };
}

type UseSqlImportLeaveGuardArgs = {
  /** Read live, not from render state: a save re-baselines and navigates in one tick. */
  hasUnsavedChanges: () => boolean;
  canCreate: boolean;
  isSaving: boolean;
  saveDraft: (options: { withUrl: boolean }) => Promise<boolean>;
  onDiscard: () => void;
};

/**
 * The unsaved-changes prompt: save the draft and continue, or discard. A failed save keeps the
 * user in the wizard, so an unsaved draft is never thrown away by leaving.
 */
export function useSqlImportLeaveGuard({
  hasUnsavedChanges,
  canCreate,
  isSaving,
  saveDraft,
  onDiscard,
}: UseSqlImportLeaveGuardArgs) {
  const saveAndContinue = async () => {
    if (!(await saveDraft({ withUrl: false }))) {
      throw new Error('The draft could not be saved.');
    }
  };

  const prompt = {
    hasUnsavedChanges,
    onSave: canCreate ? saveAndContinue : undefined,
    isSaveDisabled: !canCreate || isSaving,
    onDiscard,
  };
  // Hydra shell transitions and tab close/refresh.
  useNavigationGuard(prompt);
  // In-app history navigations (browser Back/Forward) and router links, which the guard above
  // never hears.
  useHistoryLeaveBlocker(prompt);
}
